/**
 * LedgerFeed (#1315) — chronological transaction ledger. One EntityTile row
 * per transaction. The COLLAPSED row carries only identity + state + money
 * (icon, label, date, status chip, Betrag, running Kontostand); clicking it
 * expands a detail panel with the category, the planned→kontrahiert→bezahlt
 * lifecycle, the plan↔Ist figures, the qty×price breakdown, the document ref,
 * and the edit / categorize actions. This keeps the dense per-row metadata
 * (transaction-records) out of the scan path until it's asked for. A
 * history→plan boundary divider and a Paginator (page state owned here) frame
 * the list.
 */
import { useEffect, useState } from 'react';
import { AlertTriangle, Check, Clock, HelpCircle, Pencil, Plus } from 'lucide-react';
import { EntityTile, Paginator } from '@hofsync/design-system';
import DirectionIcon from './DirectionIcon';
import { formatEur } from '../../../utils/formatEur';
import {
  categorizeAction,
  commitmentKind,
  commitmentRecord,
  qtyPriceBreakdown,
  signedTotal,
  type QtyPriceBreakdown,
} from './transactions';
import type { LedgerRow } from './buildLedgerRows';
import type { LiquiditaetRecord, PlannedTransaction, ReconInfo, Transaction } from './types';
import TransactionTwinLinks from './TransactionTwinLinks';
import CounterpartyName from './CounterpartyName';
import './LedgerFeed.css';

const PAGE_SIZE = 14;

/**
 * The single history→plan boundary: the first projected row that
 * immediately follows an actual one, computed over the FULL list so the
 * "Plan ab hier" divider appears exactly once — not on every all-projected
 * page (codex #1326 P2).
 */
export function boundaryRowId(rows: LedgerRow[]): string | null {
  for (let i = 1; i < rows.length; i++) {
    if (!rows[i].isActual && rows[i - 1].isActual) return rows[i].id;
  }
  return null;
}

const formatDay = (iso: string): string => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

/** The German name of the commitment the chip's verdict was reached against
 *  (#3026). A settled buyer credit has no Plan, so "Plan und Ist stimmen
 *  überein" would be a claim about a record that does not exist. */
const COMMITMENT_NOUN: Record<'planned' | 'contracted', string> = {
  planned: 'Plan',
  contracted: 'Kontrahierter Betrag',
};

/**
 * The commitment↔Ist state chip (#1414), display-only: ✓ bestätigt / ⚠ abweichend
 * (with Δ) / ⏳ geplant / ＋ ungeplant. The actionable categorize/edit controls
 * live in the expanded detail panel, so the collapsed row stays scannable.
 */
/**
 * HOF-135 §3 — a row that balances the model rather than describing a business event
 * says so, in the farmer's language.
 *
 * WF's forecast is dominated by nine rows labelled „Investitionen/Sonstiges (Residuum
 * ggü. Kontoauszug)" whose own note reads „RESIDUUM ohne identifizierte Buchungen":
 * −2.521.555 € across the year, and the entire Engpass — the projection without them is
 * +187.625 €. They rendered as ordinary Investitionen, so the number a farmer would act
 * on looked like a business decision somebody had made rather than a plug the model
 * needed. Naming it is not decoration: it is the difference between „wir investieren zu
 * viel" and „wir wissen nicht, wo das Geld hingeht".
 */
function PlugChip() {
  return (
    <span
      className="liquiditaetsplanung-recon liquiditaetsplanung-recon--plug"
      title="Rechnerische Ausgleichsposition — keine identifizierte Buchung. Nicht als Geschäftsvorfall lesen."
    >
      <HelpCircle size={13} aria-hidden="true" /> Rechenposten
    </span>
  );
}

function ReconChip({ recon }: { recon: ReconInfo }) {
  const noun = COMMITMENT_NOUN[recon.commitment ?? 'planned'];
  if (recon.state === 'bestaetigt') {
    return (
      <span className="liquiditaetsplanung-recon liquiditaetsplanung-recon--ok" title={`${noun} und Ist stimmen überein`}>
        <Check size={13} aria-hidden="true" /> bestätigt
      </span>
    );
  }
  if (recon.state === 'abweichend') {
    return (
      <span className="liquiditaetsplanung-recon liquiditaetsplanung-recon--warn">
        <AlertTriangle size={13} aria-hidden="true" /> abweichend
        {recon.deltaEur != null && (
          <span className="liquiditaetsplanung-recon__delta">
            {' '}· Δ {recon.deltaEur > 0 ? '+' : ''}
            {formatEur(recon.deltaEur)}
          </span>
        )}
      </span>
    );
  }
  if (recon.state === 'ungeplant') {
    return (
      <span
        className="liquiditaetsplanung-recon liquiditaetsplanung-recon--new"
        // #3026: the state now means no commitment of EITHER kind — no Plan and no
        // Beleg — so the old "Gebucht, aber nicht geplant" understated it.
        title="Gebucht, ohne Plan und ohne Beleg"
      >
        <Plus size={13} aria-hidden="true" /> ungeplant
      </span>
    );
  }
  return (
    <span className="liquiditaetsplanung-recon liquiditaetsplanung-recon--pending" title="Noch nicht gebucht">
      <Clock size={13} aria-hidden="true" /> geplant
    </span>
  );
}

const STAGES: { role: 'planned' | 'contracted' | 'payed'; label: string }[] = [
  { role: 'planned', label: 'Geplant' },
  { role: 'contracted', label: 'Kontrahiert' },
  { role: 'payed', label: 'Bezahlt' },
];

/**
 * The planned → kontrahiert → bezahlt lifecycle (transaction-records), shown in
 * the detail panel. Present stages carry a filled dot in the row's ink; absent
 * ones stay muted — so the farmer sees how far a transaction has travelled and
 * whether a Rechnung/Gutschrift (contracted) was recorded between plan and pay.
 */
function LifecycleStrip({ tx }: { tx: Transaction }) {
  return (
    <span className="liquiditaetsplanung-ledger__lifecycle" aria-label="Lebenszyklus">
      {STAGES.map(({ role, label }) => {
        const rec = tx[role];
        return (
          <span
            key={role}
            className={`liquiditaetsplanung-ledger__stage${rec ? ' liquiditaetsplanung-ledger__stage--present' : ''}`}
            title={rec ? `${label}: ${formatEur(signedTotal(rec))}` : `${label}: –`}
          >
            <span className="liquiditaetsplanung-ledger__dot" aria-hidden="true" />
            {label}
          </span>
        );
      })}
    </span>
  );
}

/** First record (planned → payed → contracted) carrying a real qty×price breakdown. */
function breakdownOf(tx?: Transaction): QtyPriceBreakdown | null {
  if (!tx) return null;
  for (const rec of [tx.planned, tx.payed, tx.contracted] as (LiquiditaetRecord | undefined)[]) {
    if (rec) {
      const b = qtyPriceBreakdown(rec);
      if (b) return b;
    }
  }
  return null;
}

/**
 * The expanded detail panel: the per-row metadata kept out of the collapsed
 * scan path — category, lifecycle, plan↔Ist figures, qty×price, document ref —
 * plus the edit (planned) / categorize (ungeplant) actions.
 */
/** The row's owning account id, if any: taken from its transaction's records (bank
 *  rows carry it from the wire; user plans from the write path) with the derived
 *  ``row.planned`` as a last resort. ``undefined`` for an account-less row. */
function accountIdOf(row: LedgerRow): string | undefined {
  const tx = row.transaction;
  return (
    tx?.planned?.account_id ??
    tx?.payed?.account_id ??
    tx?.contracted?.account_id ??
    row.planned?.account_id
  );
}

/** The row's linked ``organization``, if any (#3143) — the FARMER's pick first.
 *
 *  That order is the authorship rule from ``backend/ARCH.md`` § `cash_flow.counterparty_id`:
 *  the FACT is write-of-record for a farmer's pick, the entity attribute for the
 *  machine-written lane. So a plan outranks the contracted record beside it, and the
 *  surface stays forward-compatible with #3142's derived links. Note this differs
 *  deliberately from ``accountIdOf``'s planned → payed → contracted: an account is a
 *  routing fact where the BOOKING is the better authority, a counterparty is an
 *  authorship one. ``undefined`` for an unlinked row, which renders no Gegenpartei.
 *
 *  #3160 widened the return to the ``{ id, source }`` PAIR, read off the SAME
 *  record that won the id walk — never one record's id with a sibling's source.
 *  Only bank ``payed`` records ever carry ``counterparty_id_source``; if the
 *  planned record's id wins (a farmer's pick), attaching the payed sibling's
 *  machine source would label that pick „automatisch zugeordnet" — exactly the
 *  laundering the #3142 provenance design exists to prevent. ``row.planned`` is a
 *  ``PlannedTransaction``, which is structurally source-less (Python mirror
 *  parity), so its fallback arm never emits a source. */
function counterpartyOf(row: LedgerRow): { id: string; source?: string } | undefined {
  const tx = row.transaction;
  for (const rec of [tx?.planned, tx?.contracted, tx?.payed] as (LiquiditaetRecord | undefined)[]) {
    if (rec?.counterparty_id) {
      return { id: rec.counterparty_id, source: rec.counterparty_id_source };
    }
  }
  const planned = row.planned?.counterparty_id;
  return planned ? { id: planned } : undefined;
}

function RowDetail({
  row,
  isExpanded,
  onEditPlanned,
  onCategorize,
  accountLabelById,
}: {
  row: LedgerRow;
  /** Only the open row mounts the twin-links read so it fires once, not per row. */
  isExpanded: boolean;
  onEditPlanned?: (planned: PlannedTransaction) => void;
  onCategorize?: (row: LedgerRow) => void;
  /** account_id → label lookup (#2211). Passed ONLY by multi-account tenants, so a
   *  single-account render shows no Konto row (markup byte-identical). */
  accountLabelById?: Map<string, string>;
}) {
  const tx = row.transaction;
  const breakdown = breakdownOf(tx);
  // The Konto row shows only when a lookup is passed AND the row resolves to a
  // KNOWN account — a stale/unknown id is dropped rather than shown raw.
  const accountId = accountIdOf(row);
  const kontoLabel = accountLabelById && accountId ? accountLabelById.get(accountId) : undefined;
  const counterparty = counterpartyOf(row);
  // The commitment the Ist is reported against — plan first, else the CONTRACT
  // (#3026), from the SAME helper ``transactionState``/``recon.commitment`` use, so
  // the panel can never name a different figure than the chip judged against. A
  // settled buyer payment has no plan, so reading ``planned`` alone rendered
  // "kein Plan" beside a bestätigt/abweichend chip that had judged it by the invoice.
  const commitment = tx ? commitmentRecord(tx) : undefined;
  // The NOUN comes from the same helper as the value (#3173). Reading ``tx.planned``
  // directly was a latent second definition: on a #3152-grouped row (planned +
  // contracted + payed) the contract judges and ``commitmentRecord`` returns the
  // contracted figure, so "Plan" would have labelled a number taken from elsewhere.
  const commitmentLabel = tx && commitmentKind(tx) === 'planned' ? 'Plan' : 'Kontrahiert';
  // An uncommitted row (payed only) has NO figure to name, so it must not borrow
  // the contracted noun for its heading while its value reads "kein Plan" (#3173).
  const commitmentTerm = commitment ? `${commitmentLabel} ↔ Ist` : 'Ist';
  const plan = commitment ? signedTotal(commitment) : null;
  const ist = tx?.payed ? signedTotal(tx.payed) : null;
  const delta = row.recon?.deltaEur;
  const docRef = tx?.contracted?.doc_ref;
  // Notes can differ per lifecycle stage (a plan rationale vs. a deviation
  // note on the booking), so gather the distinct ones across the records.
  const notes = [...new Set(
    [tx?.planned?.notes, tx?.contracted?.notes, tx?.payed?.notes, row.planned?.notes]
      .map((n) => n?.trim())
      .filter((n): n is string => !!n),
  )];

  const canEdit = row.planned && onEditPlanned;
  // #3140 — routed through the SHARED predicate (#3065 left this as its own narrower
  // `recon.state === 'ungeplant'` check on SCOPE grounds; #3107 changed that premise
  // by making the contracted-only shape render at all). `categorizeAction` carries
  // the `settled_at` money guard, so an already-settled expectation still offers
  // nothing here — see its docstring.
  const canCategorize = categorizeAction(row) === 'categorize' && onCategorize;

  return (
    <div className="liquiditaetsplanung-ledger__detail">
      <dl className="liquiditaetsplanung-ledger__detail-grid">
        {row.sublabel && (
          <>
            <dt>Kategorie</dt>
            <dd>{row.sublabel}</dd>
          </>
        )}
        {kontoLabel && (
          <>
            <dt>Konto</dt>
            <dd>{kontoLabel}</dd>
          </>
        )}
        {/* #3143 — gated on ``isExpanded`` for the SAME reason TransactionTwinLinks
            is (below): ``CounterpartyName`` is a READ, and EntityTile mounts its
            children eagerly, so an ungated mount is an N+1 across the page and reds
            every provider-less test in ``LedgerFeed.test.tsx``. */}
        {isExpanded && counterparty && (
          <>
            <dt>Gegenpartei</dt>
            <dd>
              {/* #3160: the source is the WINNING record's own (see counterpartyOf).
                  CounterpartyName renders it as a data-counterparty-source attribute
                  only — the visible marker stays #3142 Phase 2's. */}
              <CounterpartyName id={counterparty.id} source={counterparty.source} />
            </dd>
          </>
        )}
        {tx && (
          <>
            <dt>Lebenszyklus</dt>
            <dd>
              <LifecycleStrip tx={tx} />
            </dd>
          </>
        )}
        {(plan != null || ist != null) && (
          <>
            {/* #3026: the term follows its value — a plan-less settled invoice
                reports "Kontrahiert … → Ist …", so "Plan ↔ Ist" would mis-name it. */}
            <dt>{commitmentTerm}</dt>
            <dd className="liquiditaetsplanung-ledger__planist">
              {plan != null && ist != null ? (
                <>
                  {commitmentLabel} {formatEur(plan)} → Ist {formatEur(ist)}
                  {delta != null && (
                    <span className="liquiditaetsplanung-recon__delta">
                      {'  '}· Δ {delta > 0 ? '+' : ''}
                      {formatEur(delta)}
                    </span>
                  )}
                </>
              ) : plan != null ? (
                <>
                  {commitmentLabel} {formatEur(plan)} · noch nicht gebucht
                </>
              ) : (
                <>Ist {formatEur(ist!)} · kein Plan</>
              )}
            </dd>
          </>
        )}
        {breakdown && (
          <>
            <dt>Menge × Preis</dt>
            <dd className="liquiditaetsplanung-ledger__breakdown">
              {breakdown.qty.toLocaleString('de-DE')} × {formatEur(breakdown.unitPriceEur)}
            </dd>
          </>
        )}
        {docRef && (
          <>
            <dt>Beleg</dt>
            <dd>{docRef}</dd>
          </>
        )}
        {notes.length > 0 && (
          <>
            <dt>Notiz</dt>
            <dd className="liquiditaetsplanung-ledger__notes">
              {notes.map((n, i) => (
                <p key={i}>{n}</p>
              ))}
            </dd>
          </>
        )}
      </dl>
      {isExpanded && tx && <TransactionTwinLinks transactionId={tx.id} />}
      {(canEdit || canCategorize) && (
        <div className="liquiditaetsplanung-ledger__detail-actions">
          {canEdit && (
            <button
              type="button"
              className="liquiditaetsplanung-ledger__action"
              aria-label={`${row.label} bearbeiten`}
              onClick={(e) => {
                e.stopPropagation();
                onEditPlanned!(row.planned!);
              }}
            >
              <Pencil size={14} aria-hidden="true" /> Bearbeiten
            </button>
          )}
          {canCategorize && (
            <button
              type="button"
              className="liquiditaetsplanung-ledger__action liquiditaetsplanung-ledger__action--primary"
              aria-label="kategorisieren"
              onClick={(e) => {
                e.stopPropagation();
                onCategorize!(row);
              }}
            >
              <Plus size={14} aria-hidden="true" /> kategorisieren
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function LedgerFeed({
  rows,
  kkLimitEur,
  onEditPlanned,
  onCategorize,
  selectedId,
  onSelect,
  accountLabelById,
}: {
  rows: LedgerRow[];
  /** KK-limit (overdraft floor): rows whose running balance drops below it are
   *  flagged red, tying the banner warning to the ledger evidence. */
  kkLimitEur?: number | null;
  /** When set, planned rows get an edit action that opens the form pre-filled. */
  onEditPlanned?: (planned: PlannedTransaction) => void;
  /** Called when an ＋ ungeplant bank line's "kategorisieren" action is used. */
  onCategorize?: (row: LedgerRow) => void;
  /** Currently-selected ledger-row id (mirrors the chart selection). */
  selectedId?: string | null;
  /** Called with a row id when the user selects a row. */
  onSelect?: (id: string) => void;
  /** account_id → label lookup (#2211). Passed ONLY by multi-account tenants to
   *  surface each row's Konto in its detail panel; absent → no Konto row. */
  accountLabelById?: Map<string, string>;
}) {
  const [page, setPage] = useState(0);
  // Which row's detail panel is open (accordion — one at a time keeps the list
  // compact). Independent of the chart selection: scrubbing the chart tints a
  // row but does not force its detail open.
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // When the list changes (e.g. the selected month switches) reset to the
  // first page — but jump to the page holding the selected row when it's
  // present (e.g. selected from the chart). The page just renders that row
  // highlighted; it does NOT scroll the viewport to it.
  useEffect(() => {
    const idx = selectedId ? rows.findIndex((r) => r.id === selectedId) : -1;
    setPage(idx >= 0 ? Math.floor(idx / PAGE_SIZE) : 0);
  }, [selectedId, rows]);

  // A month switch (new list) collapses any open detail panel.
  useEffect(() => {
    setExpandedId(null);
  }, [rows]);

  if (rows.length === 0) {
    return <div className="liquiditaetsplanung-ledger__empty">Keine Buchungen vorhanden.</div>;
  }

  // Clamp against a stale page from the previous (longer) list so a month
  // switch never renders an out-of-range empty slice before the effect runs.
  const safePage = Math.min(page, Math.ceil(rows.length / PAGE_SIZE) - 1);
  const start = safePage * PAGE_SIZE;
  const slice = rows.slice(start, start + PAGE_SIZE);
  // Boundary computed over the passed list, so it renders once on whatever
  // page the actual→plan transition row lands (only in the month that
  // straddles today, once the list is month-filtered).
  const boundaryId = boundaryRowId(rows);

  return (
    <div className="liquiditaetsplanung-ledger">
      <div className="liquiditaetsplanung-ledger__rows">
        {slice.map((row, i) => {
          const income = row.amountEur >= 0;
          const isSelected = row.id === selectedId;
          const isExpanded = row.id === expandedId;
          const belowLimit = kkLimitEur != null && row.balanceEur < kkLimitEur;
          return (
            <div key={row.id} className="liquiditaetsplanung-ledger__row">
              {row.id === boundaryId && (
                <div className="liquiditaetsplanung-ledger__boundary">Plan ab hier</div>
              )}
              <EntityTile
                expandable
                isExpanded={isExpanded}
                onToggle={() => {
                  onSelect?.(row.id);
                  setExpandedId((cur) => (cur === row.id ? null : row.id));
                }}
                isEvenRow={i % 2 === 0}
                className={[
                  belowLimit ? 'liquiditaetsplanung-ledger__tile--below-limit' : '',
                  isSelected ? 'liquiditaetsplanung-ledger__tile--selected' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                icon={<DirectionIcon income={income} />}
                iconVariant={income ? 'success' : 'warning'}
                title={row.label}
                subtitle={formatDay(row.date)}
                tags={
                  <span className="liquiditaetsplanung-ledger__figures">
                    {row.recon && <ReconChip recon={row.recon} />}
                    {/* HOF-135 §3 — beside the state chip, not instead of it: a plug is
                        still geplant/bestätigt like any other row, it just isn't a
                        business event. Driven off the plan's own ``is_plug``, so a row
                        that never claimed to be one can never acquire the mark. */}
                    {row.planned?.is_plug && <PlugChip />}
                    <span className="liquiditaetsplanung-ledger__money">
                      <span
                        className={[
                          'liquiditaetsplanung-ledger__amount',
                          `liquiditaetsplanung-ledger__amount--${income ? 'in' : 'out'}`,
                          row.didNotHappen ? 'liquiditaetsplanung-ledger__amount--ghost' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        title={
                          row.didNotHappen
                            ? 'Geplant, aber nicht gebucht — wirkt sich nicht auf den Saldo aus'
                            : undefined
                        }
                      >
                        {income ? '+' : ''}
                        {formatEur(row.amountEur)}
                      </span>
                      <span
                        className={`liquiditaetsplanung-ledger__balance${
                          belowLimit ? ' liquiditaetsplanung-ledger__balance--below-limit' : ''
                        }`}
                        title={belowLimit ? 'Unter KK-Limit' : undefined}
                      >
                        {formatEur(row.balanceEur)}
                      </span>
                    </span>
                  </span>
                }
              >
                <RowDetail
                  row={row}
                  isExpanded={isExpanded}
                  onEditPlanned={onEditPlanned}
                  onCategorize={onCategorize}
                  accountLabelById={accountLabelById}
                />
              </EntityTile>
            </div>
          );
        })}
      </div>
      <Paginator
        currentPage={safePage}
        pageSize={PAGE_SIZE}
        totalItems={rows.length}
        onPageChange={setPage}
      />
    </div>
  );
}
